<template>
  <b-popover
    :target="`compliance-${targetId}`"
    variant="primary"
    triggers="hover"
    placement="left"
    custom-class="rounded-circle mw-100 w-auto"
  >
    <div class="d-flex align-items-center justify-content-between mw-auto w-auto border-bottom p-75" style="gap: 1rem">
      <font-awesome-icon v-if="complianceData.locked" icon="fa-regular fa-lock" />
      <h5 class="ml-0 pl-0 font-weight-medium mb-0 w-fit">{{ studentName }}'s Compliance</h5>

      <b-badge
        :variant="`${resolveComplianceScoreVariant({
          score: sectionsData ? complianceData.scores.overall : complianceData.scores.overall,
        })}`"
        class="text-capitalize"
      >
        {{
          gradeScore({
            score: sectionsData ? complianceData.scores.overall : complianceData.scores.overall,
            precision: 2,
            percent: true,
            status: sectionsData ? complianceData.status : complianceData.status,
          })
        }}
        Overall
      </b-badge>
    </div>

    <div v-if="!sectionsData" class="d-flex flex-column p-75 w-100" style="gap: 0.625rem">
      <div v-if="selectModule('attendance')" class="d-flex align-items-center justify-content-between w-100">
        <span class="d-flex align-items-center" style="gap: 0.625rem">
          <font-awesome-icon icon="fa-solid fa-user" class="text-info" style="width: 1rem; height: 1rem" />
          <p class="d-flex mb-0">Attendance</p>
        </span>
        <b-badge
          :variant="`${resolveComplianceScoreVariant({
            score: selectModule('attendance').score,
          })}`"
          class="d-flex text-capitalize"
        >
          {{
            gradeScore({
              score: selectModule('attendance').score,
              precision: 2,
              percent: true,
            })
          }}
        </b-badge>
      </div>

      <div v-if="selectModule('simulations')" class="d-flex align-items-center justify-content-between w-100">
        <span class="d-flex align-items-center" style="gap: 0.625rem">
          <font-awesome-icon icon="fa-solid fa-atom-simple" class="text-danger" />
          <p class="d-flex mb-0">Simulations</p>
        </span>
        <b-badge
          :variant="`${resolveComplianceScoreVariant({
            score: selectModule('simulations').score,
          })}`"
          class="d-flex text-capitalize"
        >
          {{
            gradeScore({
              score: selectModule('simulations').score,
              precision: 2,
              percent: true,
            })
          }}
        </b-badge>
      </div>

      <div v-if="selectModule('examedge')" class="d-flex align-items-center justify-content-between w-100">
        <span class="d-flex align-items-center" style="gap: 0.625rem">
          <font-awesome-icon icon="fa-solid fa-file-lines" class="text-warning" style="width: 1rem; height: 1rem" />
          <p class="d-flex mb-0">ExamEdge</p>
        </span>
        <b-badge
          :variant="`${resolveComplianceScoreVariant({
            score: selectModule('examedge').score,
          })}`"
          class="d-flex text-capitalize"
        >
          {{
            gradeScore({
              score: selectModule('examedge').score,
              precision: 2,
              percent: true,
            })
          }}
        </b-badge>
      </div>

      <div v-if="selectModule('labs')" class="d-flex align-items-center justify-content-between w-100">
        <span class="d-flex align-items-center" style="gap: 0.625rem">
          <font-awesome-icon icon="fa-solid fa-flask-gear" style="width: 1rem; height: 1rem; color: #7367f0" />
          <p class="d-flex mb-0">Labs</p>
        </span>
        <b-badge
          :variant="`${resolveComplianceScoreVariant({
            score: selectModule('labs').score,
          })}`"
          class="d-flex text-capitalize"
        >
          {{ gradeScore({ score: selectModule('labs').score, precision: 2, percent: true }) }}
        </b-badge>
      </div>

      <div v-if="selectModule('labs_external')" class="d-flex align-items-center justify-content-between w-100">
        <span class="d-flex align-items-center" style="gap: 0.625rem">
          <font-awesome-icon icon="fa-solid fa-flask-gear" style="width: 1rem; height: 1rem; color: #7367f0" />
          <p class="d-flex mb-0">Labs (external)</p>
        </span>
        <b-badge
          :variant="`${resolveComplianceScoreVariant({
            score: selectModule('labs_external').score,
          })}`"
          class="d-flex text-capitalize"
        >
          {{ gradeScore({ score: selectModule('labs_external').score, precision: 2, percent: true }) }}
        </b-badge>
      </div>

      <div v-if="selectModule('other')" class="d-flex align-items-center justify-content-between w-100">
        <span class="d-flex align-items-center" style="gap: 0.625rem">
          <font-awesome-icon icon="fa-solid fa-flask-gear" style="width: 1rem; height: 1rem; color: #7367f0" />
          <p class="d-flex mb-0">Other</p>
        </span>
        <b-badge
          :variant="`${resolveComplianceScoreVariant({
            score: selectModule('other').score,
          })}`"
          class="d-flex text-capitalize"
        >
          {{ gradeScore({ score: selectModule('other').score, precision: 2, percent: true }) }}
        </b-badge>
      </div>
    </div>

    <div v-else-if="complianceData.sections" class="d-flex flex-column">
      <div v-for="section in _sectionsData" :key="section.id" class="d-flex flex-column p-75" style="gap: 0.3rem">
        <p class="d-flex mb-0">{{ stripAfterPipe(section.name) }}</p>

        <div class="d-flex align-items-center" style="gap: 0.4rem">
          <font-awesome-icon v-if="complianceData.sections[section.id].locked" icon="fa-regular fa-lock" />
          <b-badge :variant="`light-${resolveCourseSectionStatusVariant(section.status)}`" class="text-capitalize">
            {{ section.status }}
          </b-badge>

          <b-badge :variant="`light-${resolveProfileStatusVariant(section.status)}`" class="text-capitalize">
            {{ title(complianceData.sections[section.id].status) }}
          </b-badge>

          <b-badge
            :variant="`${resolveComplianceScoreVariant({
              score: complianceData.sections[section.id].scores.overall,
              status: complianceData.sections[section.id].status,
            })}`"
            class="d-flex text-capitalize"
            style="width: fit-content"
          >
            {{
              gradeScore({
                score: complianceData.sections[section.id].scores.overall,
                status: complianceData.sections[section.id].status,
                precision: 2,
                percent: true,
              })
            }}
          </b-badge>
        </div>
      </div>
    </div>
  </b-popover>
</template>

<script>
import { BAvatar, BBadge, BPopover } from 'bootstrap-vue';
import { avatarText, gradeScore, title } from '@core/utils/filter';
import useCourseSections from '../../useCourseSections';
import useProfiles from '@/views/apps/roster/profile/useProfiles';

export default {
  components: {
    BBadge,
    BPopover,
    BAvatar,
  },
  props: {
    targetId: { type: String, required: true },
    studentName: { type: String, required: true },
    complianceData: { type: Object, required: true },
    sectionsData: { type: Array },
    sectionId: { type: String, required: false },
    sectionData: { type: String, required: false }, // intended for individual course sections
    profileData: { type: Object },
  },
  methods: {
    title,
    selectModule(module) {
      if (!this.complianceData || !this.complianceData.scores.modules) return null;
      return this.complianceData.scores.modules[module] || null;
    },
    stripAfterPipe(name) {
      if (!name) return;
      return name.replace(/ \| .*/, '');
    },
  },
  computed: {
    _sectionsData() {
      const sections = this.sectionsData;
      const profileSections = this.profileData?.sections;

      if (!sections || !profileSections) return null;

      return sections.map((section) => {
        const replacedId = profileSections[section.id]?.replacedBy;
        const replacement = replacedId ? profileSections[replacedId] : null;

        const base = replacement || section;
        const id = replacement ? replacedId : section.id;

        return {
          ...base,
          id,
          name: this.stripAfterPipe(section.name),
        };
      });
    },
  },
  mounted() {
    console.log('> compliance popover', this.complianceData);
    console.log('> -- using sections data:', this.sectionsData);
  },
  setup() {
    const { resolveComplianceScoreVariant, resolveProfileStatusVariant } = useProfiles();
    const { resolveCourseSectionStatusVariant } = useCourseSections();

    return {
      resolveComplianceScoreVariant,
      resolveProfileStatusVariant,
      resolveCourseSectionStatusVariant,
      gradeScore,
      avatarText,
    };
  },
};
</script>
